import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight, ChevronLeft, Terminal, Sparkles, Loader2, Globe2, Users,
  Landmark, Activity, AlertCircle, Radar, HandCoins, Eye, MapPin, Download, Link2, Check,
} from "lucide-react";
import usePageview, { getVisitorSession } from "@/lib/usePageview";

// Phase 26.1 — NGO Intelligence for Africa. Public live-data module:
// World Bank demographics + IATI activity feed + DARA Engine planning brief.
// No login. Lead-gated brief only.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtNum = (v) => {
  if (v === null || v === undefined) return "—";
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return Number(v).toLocaleString(undefined, { maximumFractionDigits: 1 });
};
const fmtUsd = (v) => {
  if (!v) return "—";
  if (v >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${Math.round(v)}`;
};

const SECTION_META = [
  { key: "headline", label: "// HEADLINE" },
  { key: "operating_landscape", label: "// OPERATING LANDSCAPE" },
  { key: "gap_analysis", label: "// GAP ANALYSIS" },
  { key: "allocation_playbook", label: "// ALLOCATION PLAYBOOK · NEXT QUARTER" },
  { key: "watch", label: "// WATCH · NEXT 30 DAYS" },
];

export default function NGOIntelligence() {
  usePageview("/ngo-intelligence");

  const [countries, setCountries] = useState([]);
  const [iso3, setIso3] = useState(() => {
    const q = new URLSearchParams(window.location.search).get("country");
    return q && /^[A-Z]{3}$/i.test(q) ? q.toUpperCase() : "NGA";
  });
  const [overview, setOverview] = useState(null);
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [statesData, setStatesData] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [stateSearch, setStateSearch] = useState("");
  const [form, setForm] = useState({ org_name: "", email: "", name: "", region_focus: "", program_focus: "", question: "" });
  const [generating, setGenerating] = useState(false);
  const [brief, setBrief] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/ngo-intel/countries`).then(({ data }) => setCountries(data.countries)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoadingOverview(true);
    setOverview(null);
    setStatesData(null);
    setSelectedState(null);
    setStateSearch("");
    axios.get(`${API}/public/ngo-intel/overview/${iso3}`, { timeout: 45000 })
      .then(({ data }) => setOverview(data))
      .catch(() => toast.error("Couldn't load live country data. Try again."))
      .finally(() => setLoadingOverview(false));
    axios.get(`${API}/public/ngo-intel/states/${iso3}`, { timeout: 60000 })
      .then(({ data }) => setStatesData(data))
      .catch(() => setStatesData({ states: [], available: false }));
  }, [iso3]);

  const pickState = (s) => {
    setSelectedState(s);
    setForm((f) => ({ ...f, region_focus: s.name }));
    toast.success(`${s.name} selected — the brief will anchor on its denominators.`);
  };

  const generate = async () => {
    if (!form.org_name.trim() || !form.email.trim()) {
      toast.error("Organisation name and work email are required.");
      return;
    }
    setGenerating(true);
    setError(null);
    setBrief(null);
    try {
      const { data } = await axios.post(`${API}/public/ngo-intel/generate-brief`, {
        iso3, ...form, visitor_session: getVisitorSession(),
      }, { timeout: 90000 });
      setBrief(data);
      setTimeout(() => document.getElementById("ngo-brief-output")?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (e) {
      setError(e?.response?.data?.detail || "Brief generation failed. Try again in a moment.");
    } finally {
      setGenerating(false);
    }
  };

  const d = overview?.demographics || {};
  const agg = overview?.aggregates;
  const countryName = countries.find((c) => c.iso3 === iso3)?.name || iso3;
  const onepagerUrl = `${API}/public/ngo-intel/onepager/${iso3}${selectedState ? `?state=${encodeURIComponent(selectedState.name)}` : ""}`;

  const [copied, setCopied] = useState(false);
  const copyLink = async () => {
    const url = `${window.location.origin}/ngo-intelligence?country=${iso3}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied — paste it into your email or WhatsApp.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(`Copy failed — link: ${url}`);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="ngo-intelligence-page">
      {/* NAV */}
      <nav className="sticky top-0 z-50 border-b border-zinc-900 bg-black/95 backdrop-blur">
        <div className="max-w-[1600px] mx-auto flex items-center justify-between px-4 sm:px-6 py-3">
          <Link to="/" className="flex items-center gap-2" data-testid="ngo-brand-home">
            <ChevronLeft className="w-4 h-4 text-zinc-500" />
            <Terminal className="w-5 h-5 text-[#FFB000]" />
            <span className="font-heading font-black text-xl tracking-tighter">DARA</span>
            <span className="font-mono text-[10px] sm:text-[11px] text-zinc-500 ml-2 hidden sm:inline">// NGO INTELLIGENCE · AFRICA</span>
          </Link>
          <Link to="/pricing" className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-wider px-4 py-2 hover:bg-[#E59E00] transition-colors" data-testid="ngo-nav-pricing">
            Talk to DARA team
          </Link>
        </div>
      </nav>

      {/* HERO */}
      <header className="border-b border-zinc-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#10283F]/30 via-transparent to-[#FFB000]/5 pointer-events-none" />
        <div className="relative max-w-[1600px] mx-auto px-4 sm:px-6 py-10 sm:py-16">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-[0.3em] text-[#FFB000] mb-3 flex items-center gap-2">
            <Globe2 className="w-3.5 h-3.5" />
            // LIVE DATA · NO LOGIN · WORLD BANK + IATI FEEDS
          </div>
          <h1 className="font-heading font-black text-3xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-5xl">
            Plan with the data <span className="text-[#FFB000]">every funder wishes you had.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-zinc-300 max-w-3xl leading-relaxed">
            Most NGOs in Africa run programmes without a denominator — no live view of population, poverty, or what other
            organisations are already funding in their state. DARA pulls <span className="text-[#FFB000]">live World Bank demographics and the IATI aid-transparency feed</span>,
            then writes a planning brief for your region in 60 seconds.
          </p>
          <p className="mt-3 text-xs sm:text-sm text-zinc-500 max-w-3xl italic leading-relaxed">
            All figures below are retrieved live from public, authoritative sources. The brief is generated by the DARA Engine against those figures only.
          </p>
        </div>
      </header>

      {/* COUNTRY PICKER + OUTREACH TOOLBAR */}
      <div className="border-b border-zinc-900">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-4 flex items-center gap-2 flex-wrap" data-testid="ngo-country-picker">
          <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mr-2">// COUNTRY</span>
          {countries.map((c) => (
            <button
              key={c.iso3}
              onClick={() => setIso3(c.iso3)}
              data-testid={`ngo-country-${c.iso3}`}
              className={`font-mono text-[11px] uppercase tracking-wider px-3 py-1.5 border transition-colors ${
                iso3 === c.iso3
                  ? "bg-[#FFB000] text-black border-[#FFB000] font-bold"
                  : "text-zinc-300 border-zinc-800 hover:border-zinc-500 hover:text-white"
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 pb-4 flex items-center gap-2 flex-wrap" data-testid="ngo-outreach-toolbar">
          <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mr-2">// OUTREACH KIT</span>
          <a
            href={onepagerUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="ngo-save-onepager-btn"
            className="font-mono text-[11px] uppercase tracking-wider px-3 py-1.5 border border-[#FFB000] text-[#FFB000] hover:bg-[#FFB000] hover:text-black transition-colors flex items-center gap-2"
          >
            <Download className="w-3.5 h-3.5" /> Save {countryName} atlas (PDF{selectedState ? ` · ${selectedState.name}` : ""})
          </a>
          <button
            onClick={copyLink}
            data-testid="ngo-copy-link-btn"
            className="font-mono text-[11px] uppercase tracking-wider px-3 py-1.5 border border-zinc-800 text-zinc-300 hover:border-zinc-500 hover:text-white transition-colors flex items-center gap-2"
          >
            {copied ? <Check className="w-3.5 h-3.5 text-[#FFB000]" /> : <Link2 className="w-3.5 h-3.5" />} {copied ? "Copied" : "Copy share link"}
          </button>
          <span className="font-mono text-[10px] text-zinc-600 hidden md:inline ml-1">
            4-page atlas · maps · claim-vs-spent · fund trace · send to NNNGO members, IATI publishers, donors
          </span>
        </div>
      </div>

      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 py-8 space-y-8">
        {loadingOverview && (
          <div className="border border-zinc-900 bg-zinc-950/30 p-12 text-center" data-testid="ngo-loading-state">
            <Loader2 className="w-8 h-8 text-[#FFB000] animate-spin mx-auto mb-3" />
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000]">// PULLING LIVE WORLD BANK + IATI FEEDS…</div>
          </div>
        )}

        {overview && (
          <>
            {/* DEMOGRAPHICS TILES */}
            <section data-testid="ngo-demographics">
              <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3 flex items-center gap-2">
                <Users className="w-3.5 h-3.5" /> // {overview.country.name.toUpperCase()} · POPULATION &amp; NEED DENOMINATORS (WORLD BANK · LIVE)
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-zinc-900 border border-zinc-900">
                {Object.entries(d).map(([k, v]) => (
                  <div key={k} className="bg-black p-4" data-testid={`ngo-stat-${k}`}>
                    <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 leading-tight min-h-[22px]">{v.label}</div>
                    <div className="font-heading font-black text-xl sm:text-2xl text-white mt-1 tabular-nums">
                      {k === "gdp_per_capita" && v.value ? `$${fmtNum(v.value)}` : fmtNum(v.value)}
                      {(k === "poverty_rate" || k === "rural_pct") && v.value != null ? "%" : ""}
                    </div>
                    <div className="font-mono text-[9px] text-zinc-600 mt-0.5">{v.year ? `as of ${v.year}` : "no recent data"}</div>
                  </div>
                ))}
              </div>
            </section>

            {/* STATE DRILLDOWN */}
            <section data-testid="ngo-states-section">
              <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
                <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] flex items-center gap-2">
                  <MapPin className="w-3.5 h-3.5" /> // STATE DRILLDOWN · SUB-NATIONAL DENOMINATORS (UN OCHA COD-PS · LIVE)
                </div>
                {statesData?.available && (
                  <input
                    value={stateSearch}
                    onChange={(e) => setStateSearch(e.target.value)}
                    placeholder="Search states…"
                    data-testid="ngo-state-search"
                    className="bg-black border border-zinc-800 focus:border-[#FFB000] outline-none px-3 py-1.5 text-xs text-white placeholder:text-zinc-700 w-48 transition-colors"
                  />
                )}
              </div>

              {!statesData && (
                <div className="border border-zinc-900 bg-zinc-950/30 p-6 flex items-center gap-3" data-testid="ngo-states-loading">
                  <Loader2 className="w-4 h-4 text-[#FFB000] animate-spin" />
                  <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Pulling sub-national population feed…</span>
                </div>
              )}

              {statesData && !statesData.available && (
                <div className="border border-zinc-900 bg-zinc-950/30 p-6" data-testid="ngo-states-unavailable">
                  <p className="text-sm text-zinc-500">Sub-national population data hasn't been published for this country yet. The national denominators above still apply.</p>
                </div>
              )}

              {statesData?.available && (
                <>
                  {selectedState && (
                    <div className="border border-[#FFB000]/40 bg-[#FFB000]/5 mb-3 px-4 py-3 flex items-center gap-4 sm:gap-6 flex-wrap" data-testid="ngo-selected-state">
                      <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000]">// SELECTED · {selectedState.name.toUpperCase()}</div>
                      <div className="font-mono text-xs text-zinc-300 tabular-nums">POP {fmtNum(selectedState.population)}</div>
                      <div className="font-mono text-xs text-zinc-300 tabular-nums">WOMEN {fmtNum(selectedState.female)}</div>
                      <div className="font-mono text-xs text-zinc-300 tabular-nums">CHILDREN 0-14 {fmtNum(selectedState.children_0_14)}</div>
                      <div className="font-mono text-xs text-zinc-500 tabular-nums">{selectedState.national_share_pct}% of national</div>
                      <button onClick={() => { setSelectedState(null); setForm((f) => ({ ...f, region_focus: "" })); }} className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white ml-auto" data-testid="ngo-clear-state">
                        Clear
                      </button>
                    </div>
                  )}
                  <div className="border border-zinc-900 overflow-x-auto max-h-[360px] overflow-y-auto">
                    <table className="w-full text-xs min-w-[700px]">
                      <thead className="sticky top-0 bg-black z-10">
                        <tr className="text-left text-zinc-500 font-mono text-[10px] uppercase tracking-wider border-b border-zinc-800">
                          <th className="px-3 py-2 w-[26%]">State / region</th>
                          <th className="px-3 py-2 w-[16%] text-right">Population</th>
                          <th className="px-3 py-2 w-[16%] text-right">Women</th>
                          <th className="px-3 py-2 w-[16%] text-right">Children 0-14</th>
                          <th className="px-3 py-2 w-[26%]">Share of national</th>
                        </tr>
                      </thead>
                      <tbody>
                        {statesData.states
                          .filter((s) => s.name.toLowerCase().includes(stateSearch.toLowerCase()))
                          .map((s, i) => (
                            <tr
                              key={s.code || s.name}
                              onClick={() => pickState(s)}
                              data-testid={`ngo-state-row-${i}`}
                              className={`border-b border-zinc-900 cursor-pointer transition-colors ${selectedState?.name === s.name ? "bg-[#FFB000]/10" : "hover:bg-zinc-950"}`}
                            >
                              <td className={`px-3 py-2 ${selectedState?.name === s.name ? "text-[#FFB000] font-bold" : "text-zinc-200"}`}>{s.name}</td>
                              <td className="px-3 py-2 text-right font-mono tabular-nums text-white">{fmtNum(s.population)}</td>
                              <td className="px-3 py-2 text-right font-mono tabular-nums text-zinc-300">{fmtNum(s.female)}</td>
                              <td className="px-3 py-2 text-right font-mono tabular-nums text-zinc-300">{fmtNum(s.children_0_14)}</td>
                              <td className="px-3 py-2">
                                <div className="flex items-center gap-2">
                                  <div className="h-1.5 bg-[#FFB000]/70" style={{ width: `${Math.min(100, s.national_share_pct * 6)}px` }} />
                                  <span className="font-mono text-[10px] text-zinc-500 tabular-nums">{s.national_share_pct}%</span>
                                </div>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="font-mono text-[9px] text-zinc-600 mt-2">
                    {statesData.states.length} regions · reference year {statesData.reference_year || "—"} · {statesData.source} · click a state to anchor your planning brief on it
                  </div>
                </>
              )}
            </section>

            {/* FUNDING LANDSCAPE */}
            {agg && (
              <section data-testid="ngo-funding-landscape">
                <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3 flex items-center gap-2">
                  <Landmark className="w-3.5 h-3.5" /> // WHO ELSE IS FUNDED HERE (IATI · LIVE)
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                  <div className="lg:col-span-4 grid grid-cols-3 lg:grid-cols-1 gap-px bg-zinc-900 border border-zinc-900">
                    <div className="bg-black p-4">
                      <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">Active programmes tracked</div>
                      <div className="font-heading font-black text-2xl text-[#FFB000] mt-1 tabular-nums" data-testid="ngo-active-count">{agg.active_count}</div>
                    </div>
                    <div className="bg-black p-4">
                      <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">Total committed (USD)</div>
                      <div className="font-heading font-black text-2xl text-white mt-1 tabular-nums">{fmtUsd(agg.total_commitment)}</div>
                    </div>
                    <div className="bg-black p-4">
                      <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">Total spent (USD)</div>
                      <div className="font-heading font-black text-2xl text-white mt-1 tabular-nums">{fmtUsd(agg.total_spend)}</div>
                    </div>
                  </div>
                  <div className="lg:col-span-8 border border-zinc-900 bg-zinc-950/50 p-5">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 mb-3">// MOST ACTIVE ORGANISATIONS</div>
                    <div className="space-y-2">
                      {agg.top_orgs.map((o, i) => (
                        <div key={i} className="flex items-center gap-3" data-testid={`ngo-top-org-${i}`}>
                          <span className="font-mono text-[10px] text-zinc-600 w-5 text-right">{i + 1}.</span>
                          <div className="flex-1 text-sm text-zinc-200 truncate">{o.org}</div>
                          <div className="h-1.5 bg-[#FFB000]/70" style={{ width: `${Math.min(100, (o.count / (agg.top_orgs[0]?.count || 1)) * 100)}px` }} />
                          <span className="font-mono text-[11px] text-zinc-400 tabular-nums w-16 text-right">{o.count} active</span>
                        </div>
                      ))}
                      {agg.top_orgs.length === 0 && <div className="text-sm text-zinc-500">No active activity publishers found for this window.</div>}
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* ACTIVITY FEED */}
            <section data-testid="ngo-activity-feed">
              <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3 flex items-center gap-2">
                <Activity className="w-3.5 h-3.5" /> // LIVE ACTIVITY FEED · TOP PROGRAMMES BY COMMITMENT
              </div>
              <div className="border border-zinc-900 overflow-x-auto max-h-[420px] overflow-y-auto">
                <table className="w-full text-xs min-w-[760px]">
                  <thead className="sticky top-0 bg-black">
                    <tr className="text-left text-zinc-500 font-mono text-[10px] uppercase tracking-wider border-b border-zinc-800">
                      <th className="px-3 py-2 w-[38%]">Programme</th>
                      <th className="px-3 py-2 w-[24%]">Reporting org</th>
                      <th className="px-3 py-2 w-[12%] text-right">Committed</th>
                      <th className="px-3 py-2 w-[12%] text-right">Spent</th>
                      <th className="px-3 py-2 w-[14%]">Runs until</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overview.activities.map((a, i) => (
                      <tr key={a.aid || i} className="border-b border-zinc-900 hover:bg-zinc-950" data-testid={`ngo-activity-row-${i}`}>
                        <td className="px-3 py-2 text-zinc-200">{a.title}</td>
                        <td className="px-3 py-2 text-zinc-400">{a.org || "—"}</td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums text-[#FFB000]">{fmtUsd(a.commitment_usd)}</td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums text-zinc-300">{fmtUsd(a.spend_usd)}</td>
                        <td className="px-3 py-2 font-mono text-zinc-500">{a.end || "—"}</td>
                      </tr>
                    ))}
                    {overview.activities.length === 0 && (
                      <tr><td colSpan={5} className="px-3 py-6 text-center text-zinc-500">No active IATI-published activities found.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="font-mono text-[9px] text-zinc-600 mt-2">
                Sources: {overview.sources.join(" · ")} · retrieved {new Date(overview.fetched_at).toUTCString()}
              </div>
            </section>
          </>
        )}

        {/* BRIEF GENERATOR */}
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="ngo-brief-section">
          <div className="lg:col-span-5 border border-zinc-900 bg-zinc-950/50 p-5 space-y-3 self-start">
            <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] flex items-center gap-2">
              <Radar className="w-3.5 h-3.5" /> // GET YOUR REGIONAL PLANNING BRIEF
            </div>
            <p className="text-sm text-zinc-400 leading-relaxed">
              The DARA Engine reads the live data above and writes a gap analysis + allocation playbook for <span className="text-white">your</span> region and programme focus. Free. Delivered on this page.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <FormField label="Organisation *" value={form.org_name} onChange={(v) => setForm({ ...form, org_name: v })} placeholder="Hope Alive Initiative" testId="ngo-form-org" />
              <FormField label="Work email *" value={form.email} onChange={(v) => setForm({ ...form, email: v })} placeholder="director@ngo.org" type="email" testId="ngo-form-email" />
              <FormField label="Your name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="optional" testId="ngo-form-name" />
              <FormField label="Region / state focus" value={form.region_focus} onChange={(v) => setForm({ ...form, region_focus: v })} placeholder="e.g. Borno State" testId="ngo-form-region" />
            </div>
            <FormField label="Programme focus" value={form.program_focus} onChange={(v) => setForm({ ...form, program_focus: v })} placeholder="e.g. maternal health · WASH · girls' education" testId="ngo-form-focus" />
            <FormField label="Specific question (optional)" multiline value={form.question} onChange={(v) => setForm({ ...form, question: v })} placeholder="e.g. Where is funding thinnest relative to child population?" testId="ngo-form-question" />
            <button
              onClick={generate}
              disabled={generating}
              data-testid="ngo-generate-brief-btn"
              className="w-full bg-[#FFB000] text-black font-bold font-mono text-xs uppercase tracking-wider px-6 py-3 hover:bg-[#E59E00] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {generating ? "DARA Engine synthesising…" : "Generate planning brief"}
              {!generating && <ArrowRight className="w-4 h-4" />}
            </button>
          </div>

          <div className="lg:col-span-7" id="ngo-brief-output" data-testid="ngo-brief-output">
            {!brief && !generating && !error && (
              <div className="border border-zinc-900 bg-zinc-950/30 p-8 sm:p-12 text-center min-h-[320px] flex flex-col items-center justify-center" data-testid="ngo-brief-empty">
                <Eye className="w-12 h-12 text-zinc-700 mb-4" />
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000] mb-2">// AWAITING YOUR DETAILS</div>
                <h3 className="font-heading font-bold text-xl tracking-tight mb-2">Your planning brief will appear here.</h3>
                <p className="text-sm text-zinc-400 max-w-md leading-relaxed">
                  Landscape read · gap analysis against demographic denominators · a 4-move allocation playbook · a 30-day watchlist. ~15 seconds.
                </p>
              </div>
            )}
            {generating && (
              <div className="border border-zinc-900 bg-zinc-950/30 p-8 sm:p-12 text-center min-h-[320px] flex flex-col items-center justify-center" data-testid="ngo-brief-loading">
                <Loader2 className="w-10 h-10 text-[#FFB000] animate-spin mb-4" />
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000] mb-2">// DARA ENGINE SYNTHESISING</div>
                <h3 className="font-heading font-bold text-lg tracking-tight">Reading live feeds · mapping gaps · writing your playbook…</h3>
              </div>
            )}
            {error && !generating && (
              <div className="border border-red-900/50 bg-red-950/20 p-6" data-testid="ngo-brief-error">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-heading font-bold text-base mb-1 text-red-300">Brief generation failed</h3>
                    <p className="text-sm text-zinc-300 leading-relaxed">{String(error)}</p>
                  </div>
                </div>
              </div>
            )}
            {brief && (
              <div className="border border-zinc-900 bg-zinc-950/50" data-testid="ngo-brief-result">
                <div className="border-b border-zinc-900 px-5 py-3 flex items-center justify-between flex-wrap gap-2">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000]">
                    // DARA REGIONAL PLANNING BRIEF · {brief.country.name.toUpperCase()}
                  </div>
                  <div className="font-mono text-[9px] text-zinc-600">{new Date(brief.generated_at).toUTCString()}</div>
                </div>
                <div className="p-5 space-y-5">
                  {SECTION_META.map(({ key, label }) => (
                    <div key={key} data-testid={`ngo-brief-${key}`}>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000] mb-1.5">{label}</div>
                      <div className={`whitespace-pre-line leading-relaxed ${key === "headline" ? "font-heading font-bold text-lg text-white" : "text-sm text-zinc-300"}`}>
                        {brief.prose[key]}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="border-t border-zinc-900 px-5 py-4 flex items-center justify-between flex-wrap gap-3">
                  <p className="text-xs text-zinc-500 max-w-md">
                    Want this every Monday — plus allocation tracking against your own budget lines? That's the DARA workspace.
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <a
                      href={`${API}/public/ngo-intel/onepager/${brief.country.iso3}?brief_id=${brief.brief_id}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="ngo-brief-pdf-download"
                      className="border border-[#FFB000] text-[#FFB000] font-bold font-mono text-[11px] uppercase tracking-wider px-4 py-2 hover:bg-[#FFB000] hover:text-black transition-colors flex items-center gap-2"
                    >
                      <Download className="w-3.5 h-3.5" /> Download PDF
                    </a>
                    <Link to="/pricing" className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-wider px-4 py-2 hover:bg-[#E59E00] transition-colors flex items-center gap-2" data-testid="ngo-brief-cta">
                      <HandCoins className="w-3.5 h-3.5" /> See workspace plans
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      </main>

      {/* FOOTER STRIP */}
      <footer className="border-t border-zinc-900">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-8">
          <p className="font-mono text-[10px] text-zinc-600 leading-relaxed max-w-3xl">
            // DATA PROVENANCE — Demographics: World Bank Open Data API (most recent non-empty value per indicator).
            Aid activities: IATI — the International Aid Transparency Initiative registry, queried live via d-portal.
            Figures are presented as published by their sources; DARA does not alter source values.
          </p>
        </div>
      </footer>
    </div>
  );
}

const FormField = ({ label, value, onChange, placeholder, type = "text", multiline = false, testId }) => (
  <label className="block">
    <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 block mb-1">{label}</span>
    {multiline ? (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={2}
        data-testid={testId}
        className="w-full bg-black border border-zinc-800 focus:border-[#FFB000] outline-none px-3 py-2 text-sm text-white placeholder:text-zinc-700 transition-colors"
      />
    ) : (
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full bg-black border border-zinc-800 focus:border-[#FFB000] outline-none px-3 py-2 text-sm text-white placeholder:text-zinc-700 transition-colors"
      />
    )}
  </label>
);
